/**
 * Invite page — shows invite code, referral link, and team stats.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { binaryTreeAPI } from '../api/binary-tree';
import { useAuthStore } from '../store/auth';
import { TopBar } from '../components/ui/TopBar';
import { IconButton } from '../components/ui/IconButton';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';

interface TeamStats {
  direct_count: number;
  total_count: number;
}

export function Invite() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const profile = useAuthStore((s) => s.profile);
  const [copied, setCopied] = useState(false);

  const inviteCode = profile?.invite_code ?? '';
  const baseUrl = window.location.origin;
  const referralLink = `${baseUrl}/register?ref=${inviteCode}`;

  // Fetch team stats from binary tree API
  const { data: teamStats } = useQuery<TeamStats>({
    queryKey: ['user', 'team-stats'],
    queryFn: async () => {
      const res = (await binaryTreeAPI.getStats()) as unknown as TeamStats;
      return res as TeamStats;
    },
  });

  async function copyToClipboard(text: string) {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // fallback: select text
    }
  }

  return (
    <div className="safe-top min-h-screen bg-surface-base pb-16">
      <div className="absolute inset-0 -z-10 bg-space opacity-70" />
      <TopBar
        left={
          <IconButton aria-label={t('common.back', '返回')} onClick={() => navigate(-1)}>
            <BackIcon />
          </IconButton>
        }
        title={t('common.inviteFriend', '邀请好友')}
      />

      {/* Hero */}
      <div className="px-6 pt-6 pb-4 text-center">
        <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-2xl border border-brand-500/30 bg-brand-500/10 shadow-glow-brand">
          <span className="text-2xl">🎁</span>
        </div>
        <div className="text-lg font-semibold text-text-primary">
          {t('common.inviteHero', '邀请好友，共同成长')}
        </div>
        <div className="mt-1 text-sm text-text-secondary">
          {t('common.inviteSubtitle', '每成功邀请一位好友注册，您将获得推荐奖励')}
        </div>
      </div>

      {/* Invite code card */}
      <div className="mx-4">
        <Card className="p-5">
          <div className="text-xs text-text-secondary mb-2">{t('common.yourInviteCode', '您的邀请码')}</div>
          <div className="flex items-center justify-between rounded-xl border border-surface-border bg-surface-base/40 px-4 py-3">
            <span className="font-mono text-2xl font-bold tracking-widest text-brand-300">
              {inviteCode || '—'}
            </span>
            <Button
              type="button"
              size="sm"
              onClick={() => void copyToClipboard(inviteCode)}
              disabled={!inviteCode}
            >
              {copied ? `✓ ${t('common.copied', '已複製')}` : t('common.copy', '複製')}
            </Button>
          </div>
        </Card>
      </div>

      {/* Referral link */}
      <div className="mx-4 mt-3">
        <Card className="p-5">
          <div className="text-xs text-text-secondary mb-2">{t('common.inviteLink', '专属邀请链接')}</div>
          <div className="rounded-xl border border-surface-border bg-surface-base/40 px-3 py-2 text-xs text-text-secondary break-all font-mono">
            {referralLink}
          </div>
          <div className="mt-3">
            <Button type="button" fullWidth onClick={() => void copyToClipboard(referralLink)}>
              {copied ? `✓ ${t('common.copiedLink', '已复制链接')}` : t('common.copyInviteLink', '复制邀请链接')}
            </Button>
          </div>
        </Card>
      </div>

      {/* Team stats */}
      <div className="mx-4 mt-3">
        <Card className="p-5">
          <div className="text-xs font-semibold text-text-secondary uppercase tracking-wider mb-3">
            {t('common.myTeam', '我的团队')}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <StatCard
              label={t('common.directReferral', '直接推荐')}
              value={teamStats?.direct_count ?? 0}
              icon="👤"
            />
            <StatCard
              label={t('common.teamTotal', '团队总人数')}
              value={teamStats?.total_count ?? 0}
              icon="🌐"
            />
          </div>
        </Card>
      </div>

      {/* How it works */}
      <div className="mx-4 mt-3">
        <Card className="p-5">
          <div className="text-xs font-semibold text-text-secondary uppercase tracking-wider mb-3">
            {t('common.howToInvite', '如何邀请')}
          </div>
          <ol className="space-y-3 text-sm text-text-primary">
            <li className="flex gap-3">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-brand-500/15 text-xs font-bold text-brand-300 shrink-0">
                1
              </span>
              <span>{t('common.inviteStep1', '分享您的专属邀请码或邀请链接给朋友')}</span>
            </li>
            <li className="flex gap-3">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-brand-500/15 text-xs font-bold text-brand-300 shrink-0">
                2
              </span>
              <span>{t('common.inviteStep2', '朋友使用您的邀请码注册 ORB 账户')}</span>
            </li>
            <li className="flex gap-3">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-brand-500/15 text-xs font-bold text-brand-300 shrink-0">
                3
              </span>
              <span>{t('common.inviteStep3', '朋友完成激活后，您自动获得辅导奖励')}</span>
            </li>
          </ol>
        </Card>
      </div>
    </div>
  );
}

function BackIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 12H5M12 5l-7 7 7 7" />
    </svg>
  );
}

function StatCard({ label, value, icon }: { label: string; value: number; icon: string }) {
  return (
    <div className="rounded-xl border border-surface-border bg-surface-base/40 p-3 text-center">
      <div className="text-xl mb-1">{icon}</div>
      <div className="font-mono text-xl font-bold text-text-primary">{value}</div>
      <div className="text-xs text-text-secondary mt-0.5">{label}</div>
    </div>
  );
}
